import React from 'react';
import { SparklineProps } from '../../../types/DesignSystem/dataviz';
import { seriesClass } from '../dataviz';
import { withClassName } from '../cn';

/**
 * Sparkline (§ 311) — a trend, in the space a word would take.
 *
 * **It paints through `currentColor`.** The `<svg>` carries a `tw-text-dataviz-N`
 * class and every stroke and fill inside reads `currentColor`, which is exactly the
 * arrangement `Iconography` uses. That is what makes it theme for free: the class
 * resolves `var(--ds-dataviz-N)`, and the dark block re-points that variable. A
 * component that took a hex prop could not do this, which is the whole reason
 * ApexCharts needs a MutationObserver.
 *
 * **The viewBox equals the drawn size**, so `stroke-width="1.5"` is 1.5 real pixels.
 * A `preserveAspectRatio="none"` sparkline scaled to its container is the usual
 * shortcut and it shears the stroke — thick on one axis, thin on the other.
 */

/** Room for the endpoint dot (r=2.5) and half a stroke, so nothing clips at the box edge. */
const PAD = 3;

/**
 * Points → an SVG path in the drawn pixel space.
 *
 * Two degenerate cases have to be handled here rather than at the call site, because
 * both arrive from real data:
 *
 * - **fewer than two readings.** A new model has one day of history. Drawing nothing
 *   reads as a data bug; a flat line through the middle reads as "one reading".
 * - **every value identical.** `max - min` is 0, and the naive scale divides by it —
 *   `NaN` in a path `d`, which browsers drop silently, leaving an empty cell with no
 *   error anywhere. A flat line is the honest picture of a flat series.
 */
export const sparkPoints = (
  values: number[],
  width: number,
  height: number,
): { x: number; y: number }[] => {
  const usable = values.filter((v) => Number.isFinite(v));
  const innerW = Math.max(1, width - PAD * 2);
  const innerH = Math.max(1, height - PAD * 2);

  if (usable.length === 0) return [];
  if (usable.length === 1) {
    return [
      { x: PAD, y: PAD + innerH / 2 },
      { x: PAD + innerW, y: PAD + innerH / 2 },
    ];
  }

  const min = Math.min(...usable);
  const max = Math.max(...usable);
  const span = max - min;
  const step = innerW / (usable.length - 1);

  return usable.map((v, i) => ({
    x: PAD + i * step,
    // SVG y grows downward, so the highest value takes the smallest y.
    y: span === 0 ? PAD + innerH / 2 : PAD + innerH - ((v - min) / span) * innerH,
  }));
};

const toPath = (pts: { x: number; y: number }[]): string =>
  pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');

const Sparkline = React.forwardRef<SVGSVGElement, SparklineProps>(function Sparkline({
  values,
  label,
  decorative = false,
  series = 0,
  width = 96,
  height = 24,
  area = false,
  endpoint = true,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const pts = sparkPoints(values, width, height);
  const last = pts[pts.length - 1];

  /* An empty box still occupies its grid slot, so a loading table does not reflow
     when the data lands. Returning null here would collapse the column. */
  const hasLine = pts.length > 0;

  /* The a11y decision is the caller's (see the type file) and there is no safe
     default, so an unlabelled, non-decorative line is announced as what it is
     rather than going silent. */
  const a11y = decorative || !label
    ? { 'aria-hidden': true as const }
    : { role: 'img', 'aria-label': label };

  return (
    <svg
      {...rest}
      {...a11y}
      ref={ref}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      data-testid={dataTestId}
      className={withClassName(`${seriesClass(series)} tw-shrink-0 tw-overflow-visible`, className)}
    >
      {hasLine && area && (
        <path
          data-testid={dataTestId ? `${dataTestId}-area` : undefined}
          /* Closed down to the baseline and back. `fill-opacity` rather than a
             tinted token: the fill has to be the SAME hue as its line whichever
             series it is, and only the line's own colour can guarantee that. */
          d={`${toPath(pts)} L${last.x.toFixed(2)} ${height - PAD} L${pts[0].x.toFixed(2)} ${height - PAD} Z`}
          fill="currentColor"
          fillOpacity={0.12}
          stroke="none"
        />
      )}
      {hasLine && (
        <path
          data-testid={dataTestId ? `${dataTestId}-line` : undefined}
          d={toPath(pts)}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          /* Round, like every stroke in `Iconography` since § 244 — blunt terminals
             beside round ones read as two different sets. */
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {hasLine && endpoint && (
        <circle
          data-testid={dataTestId ? `${dataTestId}-endpoint` : undefined}
          cx={last.x}
          cy={last.y}
          r={2.5}
          fill="currentColor"
        />
      )}
    </svg>
  );
});

export default Sparkline;
